import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import LoadingScreen from "../Components/LoadingScreen";
import apiService from "../services/apiService";
import { useAuth } from "../context/AuthContext";
import { LOGO_SRC } from "../constants/branding";

const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user: currentUser, isAuthenticated, logout } = useAuth();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [accepting, setAccepting] = useState(false);
  // Inline signup state — when the recipient doesn't have an account yet,
  // we render a small form here (email locked to invite.email) instead of
  // bouncing to /signup where Google OAuth would grab their primary Google
  // identity and bypass the email-match guard.
  const [signupForm, setSignupForm] = useState({ name: "", password: "", passwordConfirm: "" });
  const [signupErrors, setSignupErrors] = useState({});
  const [signingUp, setSigningUp] = useState(false);

  // Does the currently-authenticated user's email match the invite's
  // target email? Three distinct UX states fall out of this:
  //   • not authed → inline signup form
  //   • authed AND email matches → "Accept and join" button
  //   • authed AND email DOES NOT match → "Wrong account" panel with a
  //     sign-out CTA (otherwise the user clicks Accept, gets a 403 from
  //     the backend's email-match guard, and ends up confused about why
  //     they were "taken to the sign-in form").
  const currentEmail = (currentUser?.email || "").toLowerCase().trim();
  const inviteEmail = (preview?.email || "").toLowerCase().trim();
  const emailMismatch =
    isAuthenticated && currentEmail && inviteEmail && currentEmail !== inviteEmail;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiService.getInvitePreview(token);
        if (!cancelled) {
          setPreview(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Invite not found.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const returnTo = `/invites/accept/${token}`;

  const handleSignupAndAccept = async (e) => {
    e?.preventDefault?.();
    // Cheap inline validation; the backend re-checks everything.
    const errs = {};
    if (!signupForm.name.trim()) errs.name = "Name is required.";
    if (!signupForm.password) errs.password = "Password is required.";
    else if (signupForm.password.length < 8) errs.password = "At least 8 characters.";
    if (signupForm.password !== signupForm.passwordConfirm) errs.passwordConfirm = "Passwords don't match.";
    setSignupErrors(errs);
    if (Object.keys(errs).length) return;

    setSigningUp(true);
    try {
      const result = await apiService.registerWithInvite({
        invite_token: token,
        email: preview.email,
        name: signupForm.name.trim(),
        password: signupForm.password,
        password_confirm: signupForm.passwordConfirm,
      });
      // Backend returns DRF token + org info when invite_token is valid; store
      // and drop the user into the dashboard of the org they just joined.
      if (result?.token) {
        localStorage.setItem("authToken", result.token);
      }
      toast.success(result?.message || `Welcome to ${result?.organization_name || "the organization"}!`);
      navigate("/dashboard");
    } catch (err) {
      const data = err?.response?.data;
      // Map DRF field errors back onto the form for inline display.
      if (data && typeof data === "object") {
        const mapped = {};
        if (data.invite_token) mapped.submit = Array.isArray(data.invite_token) ? data.invite_token[0] : data.invite_token;
        if (data.email) mapped.submit = Array.isArray(data.email) ? data.email[0] : data.email;
        if (data.password) mapped.password = Array.isArray(data.password) ? data.password.join(" ") : data.password;
        if (data.password_confirm) mapped.passwordConfirm = Array.isArray(data.password_confirm) ? data.password_confirm[0] : data.password_confirm;
        setSignupErrors(mapped);
      }
      toast.error(err?.message || "Could not create account. Try again.");
    } finally {
      setSigningUp(false);
    }
  };

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const result = await apiService.acceptInvite(token);
      toast.success(`Welcome to ${result?.organization_name || "the organization"}!`);
      navigate("/dashboard");
    } catch (err) {
      // The 403 invite_email_mismatch case carries expected/current email
      // in err.response.data so we can render a precise CTA instead of a
      // generic "Forbidden". Without this, the catch was a no-op
      // ("toasted by apiService" was wrong — apiService.handleError only
      // re-throws) and clicking Accept appeared to do nothing.
      const data = err?.response?.data;
      if (data?.code === "invite_email_mismatch") {
        toast.error(
          `This invite was sent to ${data.invite_email}. You're signed in as ${data.current_email}. Sign out and accept it as ${data.invite_email}.`,
          { duration: 8000 }
        );
      } else {
        toast.error(err?.message || "Could not accept invite. Try again.");
      }
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return <LoadingScreen title="Loading invite" subtitle="Verifying your link…" />;
  }

  const isPending = preview?.status === "pending";
  const expired =
    preview?.expires_at && new Date(preview.expires_at) < new Date();

  return (
    <div className="relative flex min-h-screen overflow-hidden bg-[#131313] text-white">
      <div className="pointer-events-none fixed inset-0 z-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[#131313]" />
        <div className="absolute -left-1/4 top-0 h-[min(70vh,520px)] w-[min(90vw,720px)] rounded-full bg-primary/[0.07] blur-[100px]" />
      </div>

      <div className="relative z-10 flex w-full items-center justify-center px-4 py-12 sm:px-6">
        <motion.div
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
        >
          <div className="rounded-sm border border-white/10 bg-landing-section p-8 sm:p-10">
            <div className="mb-6 text-center">
              <img
                src={LOGO_SRC}
                alt="DataNodes"
                width={127}
                height={41}
                className="mx-auto mb-4 h-10 w-auto sm:h-11"
              />
            </div>

            {error && (
              <div className="space-y-5">
                <div className="rounded-sm border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                  {error}
                </div>
                <Link
                  to="/login"
                  className="font-secondary mt-4 flex w-full items-center justify-center rounded-sm border border-white/15 px-6 py-4 text-[15px] font-semibold text-white/80 transition-colors hover:bg-white/[0.06]"
                >
                  Back to sign in
                </Link>
              </div>
            )}

            {!error && preview && !isPending && (
              <div className="space-y-5 text-center">
                <h2 className="font-title text-2xl font-bold text-white">
                  Invite no longer valid
                </h2>
                <p className="font-secondary text-[15px] text-white/65">
                  This invite has been{" "}
                  <span className="font-medium text-white/85">{preview.status}</span>
                  . Ask {preview.invited_by_name || preview.invited_by_email} to send a new one.
                </p>
                <Link
                  to="/login"
                  className="font-secondary mt-2 flex w-full items-center justify-center rounded-sm border border-white/15 px-6 py-4 text-[15px] font-semibold text-white/80 transition-colors hover:bg-white/[0.06]"
                >
                  Back to sign in
                </Link>
              </div>
            )}

            {!error && preview && isPending && expired && (
              <div className="space-y-5 text-center">
                <h2 className="font-title text-2xl font-bold text-white">
                  Invite expired
                </h2>
                <p className="font-secondary text-[15px] text-white/65">
                  Ask {preview.invited_by_name || preview.invited_by_email} to send a new invite.
                </p>
              </div>
            )}

            {!error && preview && isPending && !expired && (
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="font-title text-2xl font-bold tracking-tight text-white sm:text-[28px]">
                    Join{" "}
                    <span className="text-primary">{preview.organization_name}</span>
                  </h2>
                  <p className="font-secondary mt-3 text-[16px] leading-relaxed text-white/65">
                    {preview.invited_by_name || preview.invited_by_email} invited you
                    to join as a{" "}
                    <span className="font-medium text-white/90">{preview.role}</span>.
                  </p>
                </div>

                {emailMismatch ? (
                  /* Authenticated as a DIFFERENT email than the invite.
                     Show a clear "wrong account" panel + sign-out button
                     so the user doesn't click Accept, get a 403 from the
                     email-match guard, and end up confused. */
                  <div className="space-y-4">
                    <div className="rounded-sm border border-amber-500/30 bg-amber-500/[0.08] p-4">
                      <p className="font-secondary text-sm leading-relaxed text-amber-100/85">
                        You're signed in as{" "}
                        <span className="font-medium text-white">{currentUser?.email}</span>{" "}
                        but this invite was sent to{" "}
                        <span className="font-medium text-white">{preview.email}</span>.
                      </p>
                      <p className="font-secondary mt-2 text-sm leading-relaxed text-amber-100/70">
                        Sign out and accept the invite as {preview.email}. If no
                        account exists for that email yet, you'll be able to
                        create one on the next screen.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        try { logout(); } catch (_) {}
                        // Stay on this same route — once logged-out state
                        // is committed, the inline signup form renders.
                      }}
                      className="font-secondary flex w-full items-center justify-center rounded-sm border border-primary bg-primary px-6 py-4 text-[15px] font-bold leading-none text-[#0a0a0a] transition-colors hover:bg-primary-hover"
                    >
                      Sign out & continue
                    </button>
                  </div>
                ) : !isAuthenticated ? (
                  <form onSubmit={handleSignupAndAccept} className="space-y-4">
                    <p className="font-secondary text-center text-sm text-white/55">
                      Create your account for{" "}
                      <span className="font-medium text-white/85">{preview.email}</span>{" "}
                      to join.
                    </p>
                    <div>
                      <label htmlFor="invite-email" className="font-secondary mb-1 block text-xs uppercase tracking-wider text-white/55">
                        Email
                      </label>
                      <input
                        id="invite-email"
                        type="email"
                        value={preview.email}
                        readOnly
                        disabled
                        className="font-secondary w-full cursor-not-allowed rounded-sm border border-white/10 bg-white/[0.03] px-4 py-3 text-[14px] text-white/70"
                      />
                    </div>
                    <div>
                      <label htmlFor="invite-name" className="font-secondary mb-1 block text-xs uppercase tracking-wider text-white/55">
                        Your name
                      </label>
                      <input
                        id="invite-name"
                        type="text"
                        autoComplete="name"
                        value={signupForm.name}
                        onChange={(e) => setSignupForm((f) => ({ ...f, name: e.target.value }))}
                        className={`font-secondary w-full rounded-sm border bg-[#1a1a1a] px-4 py-3 text-[14px] text-white placeholder-white/30 outline-none transition-colors focus:border-primary ${signupErrors.name ? "border-red-500/40" : "border-white/15"}`}
                        placeholder="Kath Andújar"
                      />
                      {signupErrors.name && <p className="mt-1 text-xs text-red-300">{signupErrors.name}</p>}
                    </div>
                    <div>
                      <label htmlFor="invite-password" className="font-secondary mb-1 block text-xs uppercase tracking-wider text-white/55">
                        Password
                      </label>
                      <input
                        id="invite-password"
                        type="password"
                        autoComplete="new-password"
                        value={signupForm.password}
                        onChange={(e) => setSignupForm((f) => ({ ...f, password: e.target.value }))}
                        className={`font-secondary w-full rounded-sm border bg-[#1a1a1a] px-4 py-3 text-[14px] text-white placeholder-white/30 outline-none transition-colors focus:border-primary ${signupErrors.password ? "border-red-500/40" : "border-white/15"}`}
                        placeholder="At least 8 characters"
                      />
                      {signupErrors.password && <p className="mt-1 text-xs text-red-300">{signupErrors.password}</p>}
                    </div>
                    <div>
                      <label htmlFor="invite-password-confirm" className="font-secondary mb-1 block text-xs uppercase tracking-wider text-white/55">
                        Confirm password
                      </label>
                      <input
                        id="invite-password-confirm"
                        type="password"
                        autoComplete="new-password"
                        value={signupForm.passwordConfirm}
                        onChange={(e) => setSignupForm((f) => ({ ...f, passwordConfirm: e.target.value }))}
                        className={`font-secondary w-full rounded-sm border bg-[#1a1a1a] px-4 py-3 text-[14px] text-white placeholder-white/30 outline-none transition-colors focus:border-primary ${signupErrors.passwordConfirm ? "border-red-500/40" : "border-white/15"}`}
                      />
                      {signupErrors.passwordConfirm && <p className="mt-1 text-xs text-red-300">{signupErrors.passwordConfirm}</p>}
                    </div>
                    {signupErrors.submit && (
                      <div className="rounded-sm border border-red-500/25 bg-red-500/10 px-4 py-2 text-sm text-red-300">
                        {signupErrors.submit}
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={signingUp}
                      className="font-secondary flex w-full items-center justify-center rounded-sm border border-primary bg-primary px-6 py-4 text-[15px] font-bold leading-none text-[#0a0a0a] transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {signingUp ? "Creating account…" : `Create account & join ${preview.organization_name}`}
                    </button>
                    <p className="font-secondary text-center text-xs text-white/45">
                      Already have an account with {preview.email}?{" "}
                      <Link to={`/login?returnTo=${encodeURIComponent(returnTo)}`} className="text-primary hover:underline">
                        Sign in instead
                      </Link>
                    </p>
                  </form>
                ) : (
                  <motion.button
                    type="button"
                    onClick={handleAccept}
                    disabled={accepting}
                    className="font-secondary flex w-full items-center justify-center rounded-sm border border-primary bg-primary px-6 py-4 text-[15px] font-bold leading-none text-[#0a0a0a] transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
                    whileHover={{ scale: accepting ? 1 : 1.01 }}
                    whileTap={{ scale: accepting ? 1 : 0.99 }}
                  >
                    {accepting ? "Joining…" : `Accept and join ${preview.organization_name}`}
                  </motion.button>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AcceptInvite;
